import VSpacingContainer from 'bloko/blocks/vSpacing/VSpacingContainer';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { GbbGoodFeature } from 'src/components/Price/Gbb';
import PeriodInDays, { PeriodVariants } from 'src/components/Price/PeriodInDays';
import { useOptionsCount } from 'src/components/Price/Tariff/useOptionsCount';
import translation from 'src/components/translation';
import { Tariff } from 'src/models/price/priceZp';
import { BooleanString, IntervalInSecondsString } from 'src/models/price/product.types';
import { getOptionFeatures, isOptionProduct } from 'src/utils/price/products';

const TrlKeys = {
    searchPremiumPosition: 'price.tariffFeature.promotion.searchPremiumPosition',
    vacancyDay: 'price.tariffFeature.promotion.vacancyDay',
    premiumLabel: 'price.tariffFeature.promotion.premiumLabel',
    logo: 'price.tariffFeature.promotion.logo',
    reassignable: 'price.tariffFeature.promotion.reassignable',
    manualUpdateInterval: {
        [IntervalInSecondsString.OneDay]: 'price.tariffFeature.promotion.manualUpdateInterval.1d',
    },
};

const PromotionFeatures: TranslatedComponent<{ tariff: Tariff }> = ({ tariff, trls }) => {
    const optionsCount = useOptionsCount(tariff.product);

    // Выделение логотипом
    const renderLogoFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const hasLogo = tariff.product && getOptionFeatures(tariff.product)?.logo;
        return hasLogo === BooleanString.True ? <GbbGoodFeature>{trls[TrlKeys.logo]}</GbbGoodFeature> : null;
    };

    // На первых местах в поиске
    const renderSearchPremiumPositionFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        if (!tariff.product || !isOptionProduct(tariff.product)) {
            return null;
        }
        return <GbbGoodFeature>{trls[TrlKeys.searchPremiumPosition]}</GbbGoodFeature>;
    };

    // Выделение цветным значком
    const renderPremiumLabelFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const premiumLabel = tariff.product && getOptionFeatures(tariff.product)?.premiumLabel;
        return premiumLabel === BooleanString.True ? (
            <GbbGoodFeature>{trls[TrlKeys.premiumLabel]}</GbbGoodFeature>
        ) : null;
    };

    // Реклама на главной в разделе «Вакансии дня»
    const renderVacancyDayFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const vacancyDay = tariff.product && getOptionFeatures(tariff.product)?.vacancyDay;
        return vacancyDay && parseInt(vacancyDay, 10) ? (
            <GbbGoodFeature>{trls[TrlKeys.vacancyDay]}</GbbGoodFeature>
        ) : null;
    };

    // Можно переключать между своими вакансиями
    const renderReassignableFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice || !tariff.product || !isOptionProduct(tariff.product)) {
            return null;
        }
        return <GbbGoodFeature>{trls[TrlKeys.reassignable]}</GbbGoodFeature>;
    };

    // Поднятие на первые страницы поиска раз в 24 часа вручную
    const renderManualUpdateIntervalFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const trlKeys = TrlKeys.manualUpdateInterval;
        const interval =
            tariff.product && (getOptionFeatures(tariff.product)?.manualUpdateInterval as keyof typeof trlKeys);
        return interval && interval in trlKeys ? <GbbGoodFeature>{trls[trlKeys[interval]]}</GbbGoodFeature> : null;
    };

    // 3 дня действия
    const renderPeriodFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.period && tariff.product?.period ? (
            <GbbGoodFeature>
                <PeriodInDays period={tariff.product.period} variant={PeriodVariants.Option} />
            </GbbGoodFeature>
        ) : null;
    };

    // 2 опции «Премиум»
    const renderOptionsCountFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.optionCount && optionsCount ? <GbbGoodFeature>{optionsCount}</GbbGoodFeature> : null;
    };

    return (
        <VSpacingContainer base={4}>
            {renderOptionsCountFeature()}
            {renderPeriodFeature()}
            {renderLogoFeature()}
            {renderSearchPremiumPositionFeature()}
            {renderPremiumLabelFeature()}
            {renderVacancyDayFeature()}
            {renderReassignableFeature()}
            {renderManualUpdateIntervalFeature()}
        </VSpacingContainer>
    );

    return null;
};

export default translation(PromotionFeatures);
